import { SafePromise } from '@benzinga/safe-await';
import { proxyClientOptions, RestfulClient, Session } from '@benzinga/session';
import { FinancialsQuery, ValuationRatiosQuery } from '../entities';
import { SecuritiesEnvironment } from '../environment';
import {
  IncomingOperationRatiosResult,
  IncomingOwnershipResult,
  IncomingPeersResult,
  IncomingSecuritiesResult,
} from '../ingest';
import { securitiesRoutes } from './routes';
import { SecuritiesTransport } from './types';

const defaultPeerFields = [
  'symbol',
  'name',
  'pe',
  'forwardPERatio',
  'marketCap',
  'shareFloat',
  'sharesOutstanding',
  'dividend',
  'dividendYield',
  'exchange',
  'close',
  'changePercent',
];

export class SecuritiesRestful extends RestfulClient implements SecuritiesTransport {
  constructor(session: Session) {
    const env = session.getEnvironment(SecuritiesEnvironment);
    super(
      env.url,
      session,
      { authorizationSession: true, 'x-device-key': true },
      proxyClientOptions(undefined, 'apikey'),
    );
  }

  getFundamentals = (query: FinancialsQuery): SafePromise<IncomingSecuritiesResult> => {
    const env = this.session.getEnvironment(SecuritiesEnvironment);
    const url = this.URL(
      this.routePath(securitiesRoutes.fundamentals, env.url),
      this.withUpstreamCredentials(query, { apikey: env.key }),
    );
    return this.debouncedGet(url, { allowsAnonymousAuth: true, credentials: 'same-origin', timeout: 3000 });
  };

  getFinancials = (query: FinancialsQuery): SafePromise<IncomingSecuritiesResult> => {
    const env = this.session.getEnvironment(SecuritiesEnvironment);
    const url = this.URL(
      this.routePath(securitiesRoutes.financials, env.url),
      this.withUpstreamCredentials(query, { apikey: env.key }),
    );
    return this.debouncedGet(url, { allowsAnonymousAuth: true, credentials: 'same-origin' });
  };

  getValuationRatios = (query: ValuationRatiosQuery): SafePromise<IncomingOperationRatiosResult> => {
    const env = this.session.getEnvironment(SecuritiesEnvironment);
    const url = this.URL(
      this.routePath(securitiesRoutes.valuationRatios, env.url),
      this.withUpstreamCredentials(query, { apikey: env.key }),
    );
    return this.debouncedGet(url, { allowsAnonymousAuth: true, credentials: 'same-origin' });
  };

  getOperationRatios = (query: ValuationRatiosQuery): SafePromise<IncomingOperationRatiosResult> => {
    const env = this.session.getEnvironment(SecuritiesEnvironment);
    const url = this.URL(
      this.routePath(securitiesRoutes.operationRatios, env.url),
      this.withUpstreamCredentials(query, { apikey: env.key }),
    );
    return this.debouncedGet(url, { allowsAnonymousAuth: true, credentials: 'same-origin' });
  };

  getEarningReports = (query: ValuationRatiosQuery): SafePromise<IncomingOperationRatiosResult> => {
    const env = this.session.getEnvironment(SecuritiesEnvironment);
    const url = this.URL(
      this.routePath(securitiesRoutes.earningReports, env.url),
      this.withUpstreamCredentials(query, { apikey: env.key }),
    );
    return this.debouncedGet(url, { allowsAnonymousAuth: true, credentials: 'same-origin' });
  };

  getOwnership = (symbol: string): SafePromise<IncomingOwnershipResult> => {
    const env = this.session.getEnvironment(SecuritiesEnvironment);
    if (this.usingProxy) {
      const url = this.URL(this.routePath(securitiesRoutes.ownership), { symbols: symbol });
      return this.debouncedGet(url, { allowsAnonymousAuth: true, credentials: 'same-origin' });
    }
    const ownershipPath = new URL(securitiesRoutes.ownership.upstream, env.peersUrl).toString();
    const url = this.URL(ownershipPath, { apikey: env.key, symbols: symbol });
    return this.debouncedGet(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
      includeHeader: { authorizationSession: false, 'x-device-key': false },
    });
  };

  /** @deprecated Use ScannerManager.getRelatedSymbols or ScannerManager.getRelatedSymbolPeerQuotes instead. */
  getPeers = (symbol: string, fields: string[]): SafePromise<IncomingPeersResult> => {
    const env = this.session.getEnvironment(SecuritiesEnvironment);
    const fieldList = fields.length > 0 ? fields.join(',') : defaultPeerFields.join(',');
    if (this.usingProxy) {
      const url = this.URL(this.routePath(securitiesRoutes.peers), {
        fields: fields.length > 0 ? fieldList : undefined,
        symbol,
      });
      return this.debouncedGet(url, { allowsAnonymousAuth: true, credentials: 'same-origin' });
    }
    const peersPath = new URL(securitiesRoutes.peers.upstream, env.peersUrl).toString();
    const url = this.URL(peersPath, {
      apikey: env.peersKey || env.key,
      fields: fieldList,
      symbol,
    });
    return this.debouncedGet(url, {
      allowsAnonymousAuth: true,
      credentials: 'same-origin',
      includeHeader: { authorizationSession: false, 'x-device-key': false },
    });
  };
}
